import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Hooks from "@hooks";
import {
  Box,
  CircularProgress,
  Dialog,
  Grid,
  makeStyles,
  Theme,
  Typography,
  CssBaseline,
  TextField,
  FormHelperText,
} from "@material-ui/core";
import React from "react";
import { Error } from "@material-ui/icons";
import { Actions } from "../../../../../components";
import { Form } from "@components";

const useStyles = makeStyles((theme: Theme) => ({
  leftBar: {
    height: "100%",
    overflowX: "hidden",
    backgroundColor: theme.palette.primary.main,
  },
  noPadding: {
    padding: "0px",
  },
  root: {
    borderStyle: "none",
    borderBottom: `${theme.palette.grey[500]} solid 1px`,
  },
}));

type Props = {
  value: string;
  error: Error | undefined;
  mobile: boolean;
  name: string;
  selectedUserForTheRequest?: string;
  open: boolean;
  onAccept: () => void;
  onChange: (name: "attestation", value: string) => void;
  onDecline: () => void;
  onClose: () => void;
  readonly?: boolean;
  isAttesting: boolean;
  status?: string; // accepted, declined, ""
};

export const AttestationModal: React.FC<Props> = (props) => {
  const [settings, , settings_fetching] = Hooks.useSettings();
  const classNames = useStyles();
  const user_agreement = React.useMemo(
    () =>
      settings
        ? props.mobile
          ? settings.attestationMobile
          : settings.attestation
        : "",
    [settings, props.mobile]
  );

  // Handlers

  function handleDialogClose(): void {
    props.onClose();
  }

  function handleAttestationTextboxValueChange(
    name: "attestation",
    value: string
  ): void {
    props.onChange(name, value);
  }

  function handleDeclineButtonClick(): void {
    props.onDecline();
  }

  function handleAcceptButtonClick(): void {
    props.onAccept();
  }

  // check if requestor name and the target user is the same, then do nothing
  // else; change the placeholder to the target user
  const placeHolderName =
    props.name !== props.selectedUserForTheRequest &&
    props.selectedUserForTheRequest
      ? props.selectedUserForTheRequest
      : props.name;

  return (
    <Dialog
      fullWidth
      onClose={handleDialogClose}
      open={props.open}
      scroll="paper"
    >
      <Grid container>
        <Grid item xs={1}>
          <Box
            className={classNames.leftBar}
            justifyContent="center"
            display="flex"
            pt={5}
            p={1}
          >
            <FontAwesomeIcon
              color="#fff"
              size="lg"
              icon={faExclamationTriangle}
            ></FontAwesomeIcon>
          </Box>
        </Grid>
        <Grid container item xs={11}>
          <Box pt={5} pb={5} pr={6} pl={6}>
            <Grid item xs={12}>
              <Box alignItems="center" pb={3}>
                <Box flexGrow={1}>
                  <Typography color="primary" variant="body1">
                    Attestation
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              {settings_fetching ? (
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  color="text.disabled"
                >
                  <Box p={1}>
                    <CircularProgress size="1em" />
                  </Box>
                  <Typography variant="caption" color="inherit">
                    Loading user agreement
                  </Typography>
                </Box>
              ) : settings ? (
                <CssBaseline>
                  <div dangerouslySetInnerHTML={{ __html: user_agreement }} />
                </CssBaseline>
              ) : null}
              <Box
                display="flex"
                alignItems="center"
                height="3em"
                pt={3}
                pb={2}
              >
                <Box fontSize="h4.fontSize" fontFamily="'Arizonia', cursive">
                  {props.value}
                </Box>
              </Box>
              <Box pb={4}>
                <Grid item container>
                  <Grid item xs={6}>
                    <TextField
                      error={!!props.error}
                      fullWidth
                      name="attestation"
                      label="Please input your name here"
                      placeholder={placeHolderName}
                      onChange={(event: any) =>
                        handleAttestationTextboxValueChange(
                          event.target.name,
                          event.target.value
                        )
                      }
                      disabled={props.readonly}
                      value={props.value}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <Box hidden={!props.readonly}>
                      <Form.Checkbox
                        label="Accept"
                        name="accept"
                        disabled={true}
                        value={props.status === "accepted"}
                        size={6}
                      />
                      <Form.Checkbox
                        label="Decline"
                        name="decline"
                        disabled={true}
                        value={props.status === "declined"}
                        size={6}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box hidden={props.error === undefined}>
                      <FormHelperText
                        error={!!props.error}
                        component={Box}
                        display="flex"
                        alignItems="center"
                        pl={1}
                      >
                        <Error color="inherit" fontSize="small" />
                        <Typography
                          variant="caption"
                          component={Box}
                          pl={0.5}
                          style={{ textTransform: "none" }}
                        >
                          {props.error?.message}
                        </Typography>
                      </FormHelperText>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Actions>
                <Actions.Action
                  outlined
                  onClick={() => !!props.onClose && props.onClose()}
                >
                  Close
                </Actions.Action>
                {props.readonly ? null : (
                  <Actions.Action outlined onClick={handleDeclineButtonClick}>
                    Decline
                  </Actions.Action>
                )}
                {props.readonly ? null : (
                  <Actions.Action
                    onClick={handleAcceptButtonClick}
                    disabled={props.isAttesting}
                  >
                    Accept
                  </Actions.Action>
                )}
              </Actions>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};
